import { downloadFile } from "common/utils";
import api, { transformRequest, transformResponse } from "services/api";

export const downloadCentreApi = api
  .enhanceEndpoints({ addTagTypes: ["FileDownloads", "Documents", "DebtorSystemLogs"] })
  .injectEndpoints({
    endpoints: (build) => ({
      exportLetters: build.mutation({
        query: (payload) => ({
          url: `/api/v1/core/communication/tasks/export-letters/`,
          method: "POST",
          body: transformRequest(payload),
        }),
        transformResponse: (response) => transformResponse(response),
        invalidatesTags: [{ type: "FileDownloads", id: "LIST" }],
      }),
      downloadDebtValidationHatteras: build.mutation({
        query: (payload) => ({
          url: `/api/v1/core/debt-validation/hatteras/download/`,
          method: "POST",
          body: transformRequest(payload),
          responseHandler: (response) => response.blob(),
        }),
        transformResponse: downloadFile,
      }),
      createReport: build.mutation({
        query: (payload) => ({
          url: `/api/v1/core/credit-reports/create/`,
          method: "POST",
          body: transformRequest(payload),
        }),
        transformResponse: (response) => transformResponse(response),
        invalidatesTags: [{ type: "FileDownloads", id: "LIST" }],
      }),
      downloadInHouseLetters: build.mutation({
        query: (payload) => ({
          url: `/api/v1/core/in-house-letters/download/`,
          method: "POST",
          body: transformRequest(payload),
        }),
        transformResponse: (response) => transformResponse(response),
        invalidatesTags: [
          { type: "FileDownloads", id: "LIST" },
          // This API is also used in the Debtor Profile to download individual in house letter documents.
          // So we need to invalidate the documents and debtor system logs queries to ensure the new documents are fetched
          { type: "Documents", id: "LIST" },
          { type: "DebtorSystemLogs", id: "LIST" },
        ],
      }),
      fetchReports: build.query({
        query: () => ({
          url: `/api/v1/core/file-downloads/`,
          method: "POST",
        }),
        transformResponse: (response) => transformResponse(response),
        providesTags: (results) =>
          results
            ? // successful query
              [
                ...results.map(({ id }) => ({ type: "FileDownloads", id })),
                { type: "FileDownloads", id: "LIST" },
              ]
            : // an error occurred, but we still want to refetch this query when `{ type: 'FileDownloads', id: 'LIST' }` is invalidated
              [{ type: "FileDownloads", id: "LIST" }],
      }),
      fetchImportedFiles: build.query({
        query: () => ({
          url: `/api/v1/core/file-downloads/imported/`,
          method: "POST",
        }),
        transformResponse: (response) => transformResponse(response),
        providesTags: (results) =>
          results
            ? // successful query
              [
                ...results.map(({ id }) => ({ type: "ImportedFileDownloads", id })),
                { type: "ImportedFileDownloads", id: "LIST" },
              ]
            : // an error occurred, but we still want to refetch this query when `{ type: 'ImportedFileDownloads', id: 'LIST' }` is invalidated
              [{ type: "ImportedFileDownloads", id: "LIST" }],
      }),
      downloadAppliedInnovations: build.mutation({
        query: () => ({
          url: `/api/v1/core/applied-innovation/export/`,
          method: "POST",
        }),
        transformResponse: (response) => transformResponse(response),
        invalidatesTags: [{ type: "FileDownloads", id: "LIST" }],
      }),
      createCreditorReport: build.mutation({
        query: (payload) => ({
          url: `/api/v1/core/creditor-reports/create/`,
          method: "POST",
          body: transformRequest(payload),
        }),
        transformResponse: (response) => transformResponse(response),
        invalidatesTags: [{ type: "FileDownloads", id: "LIST" }],
      }),
    }),
    overrideExisting: false,
  });

export const {
  useFetchReportsQuery,
  useFetchImportedFilesQuery,
  useDownloadDebtValidationHatterasMutation,
  useDownloadInHouseLettersMutation,
  useCreateReportMutation,
  useExportLettersMutation,
  useDownloadAppliedInnovationsMutation,
  useCreateCreditorReportMutation,
} = downloadCentreApi;
